import React, { useEffect, useMemo, useState } from 'react';
import { Holding } from '../types';
import { formatCurrency } from '../utils/currency';
import { getCountry, COUNTRIES } from '../utils/countries';
import { Button } from './ui/Button';
import { Input, Select } from './ui/Forms';
import { Trash2, Edit2, Check, X, History, TrendingUp, TrendingDown, Minus, Search, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Filter, Pencil, Download, Power } from 'lucide-react';
interface HoldingsListProps {
  holdings: Holding[];
  onDelete: (id: string) => void;
  onUpdateAmount: (id: string, newAmount: number) => void;
  onRename: (id: string, newName: string) => void;
  onUpdateCountry: (id: string, newCountry: string) => void;
  partnerNames: [string, string];
  onExport?: () => void;
  onEditHolding?: (id: string) => void;
  onToggleActive?: (id: string) => void;
}
type SortField = 'name' | 'person' | 'type' | 'country' | 'amount' | 'createdAt';
type SortOrder = 'asc' | 'desc';
function HoldingRow({
  holding,
  onDelete,
  onUpdateAmount,
  onRename,
  onUpdateCountry,
  partnerNames,
  onEditHolding,
  onToggleActive
}: {
  holding: Holding;
  onDelete: (id: string) => void;
  onUpdateAmount: (id: string, newAmount: number) => void;
  onRename: (id: string, newName: string) => void;
  onUpdateCountry: (id: string, newCountry: string) => void;
  partnerNames: [string, string];
  onEditHolding?: (id: string) => void;
  onToggleActive?: (id: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editAmount, setEditAmount] = useState(holding.amount.toString());
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(holding.name);
  const [isEditingCountry, setIsEditingCountry] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const isFirstPartner = holding.person === partnerNames[0];
  const isJoint = holding.person === 'Joint';
  const country = getCountry(holding.country || 'US');
  const isActive = holding.isActive ?? true;
  const handleSave = () => {
    const val = Number(editAmount);
    if (!isNaN(val) && val >= 0) {
      onUpdateAmount(holding.id, val);
      setIsEditing(false);
    }
  };
  const handleCancel = () => {
    setEditAmount(holding.amount.toString());
    setIsEditing(false);
  };
  const handleSaveName = () => {
    const trimmed = editName.trim();
    if (trimmed) {
      onRename(holding.id, trimmed);
      setIsEditingName(false);
    }
  };
  const handleCancelName = () => {
    setEditName(holding.name);
    setIsEditingName(false);
  };
  const returnRate = holding.isRealEstate ? holding.appreciationRate ?? 0 : holding.expectedReturn ?? 0;
  const returnTypeLabel = holding.isRealEstate ? 'Capital Growth' : holding.returnType === 'capital-growth' ? 'Growth' : holding.returnType === 'dividend' ? 'Dividend' : holding.returnType === 'interest' ? 'Interest' : holding.returnType === 'rental-yield' ? 'Yield' : 'None';
  return <>
      {/* Mobile View */}
      <tr className={`md:hidden block border-b border-slate-100 p-4 ${!isActive ? 'opacity-50' : ''}`} data-id="element-421">
        <td className="block p-0" data-id="element-422">
          <div className="flex flex-col gap-3" data-id="element-423">
            <div className="flex justify-between items-start gap-4" data-id="element-424">
              <div className="flex flex-col" data-id="element-425">
                <span className="font-medium text-slate-900 flex items-center gap-1.5" data-id="element-426">
                  {holding.name}
                  {!isActive && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-500" data-id="element-427">
                      INACTIVE
                    </span>}
                </span>
                <span className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5" data-id="element-428">
                  {holding.isRealEstate ? 'Real Estate' : holding.type} •{' '}
                  {country.flag} {country.code}
                </span>
              </div>
              <div className="flex flex-col items-end" data-id="element-429">
                <span className="font-semibold text-slate-900" data-id="element-430">
                  {formatCurrency(holding.amount, holding.currency)}
                </span>
                <span className="text-xs font-medium text-emerald-600 mt-0.5" data-id="element-431">
                  {returnRate.toFixed(1)}% {returnTypeLabel}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-slate-50" data-id="element-432">
              <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${isJoint ? 'bg-teal-50 text-teal-700' : isFirstPartner ? 'bg-indigo-50 text-indigo-700' : 'bg-fuchsia-50 text-fuchsia-700'}`} data-id="element-433">
                {isJoint ? '🤝 Mutual' : holding.person}
              </span>
              <div className="flex items-center gap-1" data-id="element-434">
                {onToggleActive && <Button variant="ghost" size="sm" onClick={() => onToggleActive(holding.id)} className="px-2 text-slate-400" data-id="element-435">
                    <Power className="w-4 h-4" data-id="element-436" />
                  </Button>}
                <Button variant="ghost" size="sm" onClick={() => setIsExpanded(!isExpanded)} className={`px-2 ${isExpanded ? 'text-blue-600 bg-blue-50' : 'text-slate-400'}`} data-id="element-437">
                  <History className="w-4 h-4" data-id="element-438" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => {
                if (onEditHolding) onEditHolding(holding.id);else setIsEditing(true);
              }} className="px-2 text-slate-400" data-id="element-439">
                  <Edit2 className="w-4 h-4" data-id="element-440" />
                </Button>
              </div>
            </div>
          </div>
        </td>
      </tr>

      {/* Desktop View */}
      <tr className={`hidden md:table-row hover:bg-slate-50/50 transition-colors group border-b border-slate-100 last:border-0 ${!isActive ? 'opacity-50' : ''}`} data-id="element-441">
        <td className="px-4 py-4 font-medium text-slate-900" data-id="element-442">
          {isEditingName ? <div className="flex items-center gap-1.5" data-id="element-443">
              <Input className="h-8 text-sm px-2 py-1 w-full max-w-[180px]" value={editName} onChange={e => setEditName(e.target.value)} autoFocus onKeyDown={e => {
            if (e.key === 'Enter') handleSaveName();
            if (e.key === 'Escape') handleCancelName();
          }} onBlur={handleSaveName} data-id="element-444" />
            </div> : <div className="flex flex-col group/name" data-id="element-445">
              <span className="cursor-pointer hover:text-blue-600 transition-colors inline-flex items-center gap-1" onClick={() => {
            setEditName(holding.name);
            setIsEditingName(true);
          }} title="Click to rename" data-id="element-446">
                {holding.name}
                {!isActive && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-500" data-id="element-447">
                    INACTIVE
                  </span>}
                <Pencil className="w-3 h-3 text-slate-300 opacity-0 group-hover/name:opacity-100 transition-opacity" data-id="element-448" />
              </span>
              {holding.createdAt && <span className="text-xs text-slate-400 font-normal mt-0.5" data-id="element-449">
                  Added {new Date(holding.createdAt).toLocaleDateString()}
                </span>}
            </div>}
        </td>
        <td className="px-4 py-4 text-slate-600" data-id="element-450">
          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap ${isJoint ? 'bg-teal-50 text-teal-700' : isFirstPartner ? 'bg-indigo-50 text-indigo-700' : 'bg-fuchsia-50 text-fuchsia-700'}`} data-id="element-451">
            {isJoint ? '🤝 Mutual' : holding.person}
          </span>
        </td>
        <td className="px-4 py-4 text-slate-600" data-id="element-452">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${holding.isRealEstate ? 'bg-indigo-50 text-indigo-700' : holding.type === 'Liquid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-453">
            {holding.isRealEstate ? 'Real Estate' : holding.type}
          </span>
        </td>
        <td className="px-4 py-4 text-slate-600" data-id="element-454">
          {isEditingCountry ? <div className="w-48 relative z-30" data-id="element-455">
              <Select searchable className="h-8 text-sm" value={holding.country || 'US'} onChange={e => {
            onUpdateCountry(holding.id, e.target.value);
            setIsEditingCountry(false);
          }} options={COUNTRIES.map(c => ({
            value: c.code,
            label: `${c.flag} ${c.name}`
          }))} data-id="element-456" />
            </div> : <div className="flex items-center gap-2 cursor-pointer group/country" title={`${country.name} — click to change`} onClick={() => setIsEditingCountry(true)} data-id="element-457">
              <span className="text-lg leading-none" data-id="element-458">{country.flag}</span>
              <span className="text-xs font-medium text-slate-500 hidden sm:inline-block" data-id="element-459">
                {country.code}
              </span>
              <Pencil className="w-3 h-3 text-slate-300 opacity-0 group-hover/country:opacity-100 transition-opacity" data-id="element-460" />
            </div>}
        </td>
        <td className="px-4 py-4 text-slate-600" data-id="element-461">
          <div className="flex flex-col" data-id="element-462">
            <span className="text-sm font-medium text-slate-800" data-id="element-463">
              {returnRate.toFixed(1)}%
            </span>
            <span className="text-[11px] text-slate-400 font-medium mt-0.5 leading-snug" data-id="element-464">
              {returnTypeLabel}
              {(holding.capitalGainsTaxRate ?? 0) > 0 && <span className="text-slate-400" data-id="element-465">
                  {' '}
                  · {holding.capitalGainsTaxRate}% CGT
                </span>}
              {holding.isRealEstate && holding.realEstateStrategy === 'hold' && <span className="text-blue-500" data-id="element-466"> · Hold</span>}
              {holding.isRealEstate && holding.realEstateStrategy === 'sell' && <span className="text-amber-500" data-id="element-467">
                    {' '}
                    · Sell Yr {holding.plannedSaleYear ?? '?'}
                  </span>}
              {!holding.isRealEstate && holding.type === 'Illiquid' && <>
                  <span className="text-blue-500" data-id="element-468">
                    {' '}
                    ·{' '}
                    {(holding.drawdownAvailableYear ?? 0) > 0 ? `Avail. Yr ${holding.drawdownAvailableYear}` : 'Avail. at retirement'}
                  </span>
                  {holding.yieldsAfterDrawdown === false && <span className="text-amber-500" data-id="element-469"> · Sells at drawdown</span>}
                </>}
            </span>
          </div>
        </td>
        <td className="px-4 py-4 text-right font-medium text-slate-900" data-id="element-470">
          {isEditing ? <div className="flex items-center justify-end gap-2" data-id="element-471">
              <Input type="number" min="0" step="0.01" className="w-24 h-8 text-right px-2 py-1 text-sm" value={editAmount} onChange={e => setEditAmount(e.target.value)} autoFocus onKeyDown={e => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
          }} data-id="element-472" />
            </div> : <div className="flex items-center justify-end gap-2" data-id="element-473">
              {formatCurrency(holding.amount, holding.currency)}
            </div>}
        </td>
        <td className="px-4 py-4 text-center" data-id="element-474">
          {isEditing ? <div className="flex items-center justify-center gap-1" data-id="element-475">
              <Button variant="ghost" size="sm" onClick={handleSave} className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 px-2" data-id="element-476">
                <Check className="w-4 h-4" data-id="element-477" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleCancel} className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 px-2" data-id="element-478">
                <X className="w-4 h-4" data-id="element-479" />
              </Button>
            </div> : <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-480">
              {onToggleActive && <Button variant="ghost" size="sm" onClick={() => onToggleActive(holding.id)} className={`px-2 ${isActive ? 'text-slate-400 hover:text-amber-600' : 'text-amber-500 hover:text-emerald-600 !opacity-100'}`} title={isActive ? 'Deactivate' : 'Reactivate'} data-id="element-481">
                  <Power className="w-4 h-4" data-id="element-482" />
                </Button>}
              <Button variant="ghost" size="sm" onClick={() => setIsExpanded(!isExpanded)} className={`text-slate-400 hover:text-blue-600 px-2 ${isExpanded ? 'opacity-100 text-blue-600 bg-blue-50' : ''}`} title="View History" data-id="element-483">
                <History className="w-4 h-4" data-id="element-484" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => {
            if (onEditHolding) {
              onEditHolding(holding.id);
            } else {
              setIsEditing(true);
            }
          }} className="text-slate-400 hover:text-indigo-600 px-2" title="Edit All Details" data-id="element-485">
                <Edit2 className="w-4 h-4" data-id="element-486" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onDelete(holding.id)} className="text-slate-400 hover:text-red-600 px-2" title="Delete" data-id="element-487">
                <Trash2 className="w-4 h-4" data-id="element-488" />
              </Button>
            </div>}
        </td>
      </tr>
      {isExpanded && <tr className="block md:table-row bg-slate-50/80 border-b border-slate-200" data-id="element-489">
          <td colSpan={7} className="block md:table-cell px-4 md:px-6 py-4" data-id="element-490">
            <div className="text-sm" data-id="element-491">
              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2" data-id="element-492">
                <History className="w-4 h-4 text-blue-500" data-id="element-493" /> Amount History
              </h4>
              {holding.history && holding.history.length > 0 ? <div className="space-y-2 relative pl-8 md:pl-0" data-id="element-494">
                  {[...holding.history].reverse().map((entry, idx, arr) => {
              // Since we reversed, the "previous" entry chronologically is at idx + 1
              const prevEntry = idx < arr.length - 1 ? arr[idx + 1] : null;
              const diff = prevEntry ? entry.amount - prevEntry.amount : 0;
              return <div key={idx} className="relative flex items-center justify-between text-slate-600 bg-white px-4 py-2.5 rounded-lg border border-slate-100 shadow-sm max-w-md mx-auto z-10" data-id="element-495">
                        <span className="text-slate-500 text-xs font-medium" data-id="element-496">
                          {new Date(entry.date).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                        </span>
                        <div className="flex items-center gap-3" data-id="element-497">
                          <span className="font-semibold text-slate-900" data-id="element-498">
                            {formatCurrency(entry.amount, holding.currency)}
                          </span>
                          {prevEntry && <span className={`flex items-center text-xs font-bold w-20 justify-end ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-600' : 'text-slate-400'}`} data-id="element-499">
                              {diff > 0 ? <TrendingUp className="w-3.5 h-3.5 mr-1" data-id="element-500" /> : diff < 0 ? <TrendingDown className="w-3.5 h-3.5 mr-1" data-id="element-501" /> : <Minus className="w-3.5 h-3.5 mr-1" data-id="element-502" />}
                              {diff !== 0 ? formatCurrency(Math.abs(diff), holding.currency) : 'No change'}
                            </span>}
                        </div>
                      </div>;
            })}
                </div> : <p className="text-slate-500 italic text-center py-4" data-id="element-503">
                  No history available for this holding.
                </p>}
            </div>
          </td>
        </tr>}
    </>;
}
export function HoldingsList({
  holdings,
  onDelete,
  onUpdateAmount,
  onRename,
  onUpdateCountry,
  partnerNames,
  onExport,
  onEditHolding,
  onToggleActive
}: HoldingsListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPerson, setFilterPerson] = useState<string>('All');
  const [filterType, setFilterType] = useState<string>('All');
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterPerson, filterType, sortField, sortOrder]);
  const filteredAndSortedHoldings = useMemo(() => {
    let result = [...holdings];
    // 1. Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(h => h.name.toLowerCase().includes(query) || h.currency.toLowerCase().includes(query) || getCountry(h.country || 'US').name.toLowerCase().includes(query));
    }
    // 2. Filter by person
    if (filterPerson !== 'All') {
      result = result.filter(h => h.person === filterPerson);
    }
    // 3. Filter by type
    if (filterType !== 'All') {
      result = result.filter(h => h.type === filterType);
    }
    // 4. Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'person':
          comparison = a.person.localeCompare(b.person);
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'country':
          comparison = (a.country || '').localeCompare(b.country || '');
          break;
        case 'amount':
          // Note: Sorting raw amount ignores currency differences.
          // For a true sort, we'd convert to base currency first, but this is acceptable for a simple list.
          comparison = a.amount - b.amount;
          break;
        case 'createdAt':
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          comparison = dateA - dateB;
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return result;
  }, [holdings, searchQuery, filterPerson, filterType, sortField, sortOrder]);
  // Pagination
  const totalPages = Math.ceil(filteredAndSortedHoldings.length / itemsPerPage);
  const paginatedHoldings = filteredAndSortedHoldings.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-300 inline-block" data-id="element-504" />;
    return sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600 inline-block" data-id="element-505" /> : <ArrowDown className="w-3 h-3 ml-1 text-blue-600 inline-block" data-id="element-506" />;
  };
  return <div className="h-full flex flex-col" data-id="element-507">
      {/* Search and Filter Bar */}
      <div className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 flex flex-col gap-3 mb-4" data-id="element-508">
        <div className="relative flex-1" data-id="element-509">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-510" />
          <Input placeholder="Search assets, currencies, countries..." className="pl-9 h-9 text-sm bg-white" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-511" />
        </div>
        <div className="flex gap-2" data-id="element-512">
          <div className="relative z-20 flex-1 sm:flex-none sm:w-40" data-id="element-513">
            <Select className="h-9 text-sm" value={filterPerson} onChange={e => setFilterPerson(e.target.value)} options={[{
            value: 'All',
            label: 'All Owners'
          }, {
            value: partnerNames[0],
            label: partnerNames[0]
          }, {
            value: partnerNames[1],
            label: partnerNames[1]
          }, {
            value: 'Joint',
            label: '🤝 Mutual'
          }]} data-id="element-514" />
          </div>
          <div className="relative z-10 flex-1 sm:flex-none sm:w-32" data-id="element-515">
            <Select className="h-9 text-sm" value={filterType} onChange={e => setFilterType(e.target.value)} options={[{
            value: 'All',
            label: 'All Types'
          }, {
            value: 'Liquid',
            label: '💧 Liquid'
          }, {
            value: 'Illiquid',
            label: '🏢 Illiquid'
          }]} data-id="element-516" />
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-517">
        {holdings.length === 0 ? <div className="p-8 text-center text-slate-500 flex flex-col items-center justify-center h-full min-h-[300px]" data-id="element-518">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3" data-id="element-519">
              <span className="text-xl" data-id="element-520">💼</span>
            </div>
            <p data-id="element-521">No holdings added yet.</p>
            <p className="text-sm mt-1" data-id="element-522">Add your first asset using the form.</p>
          </div> : filteredAndSortedHoldings.length === 0 ? <div className="p-8 text-center text-slate-500 flex flex-col items-center justify-center h-full min-h-[300px]" data-id="element-523">
            <Search className="w-8 h-8 text-slate-300 mb-3" data-id="element-524" />
            <p data-id="element-525">No holdings match your filters.</p>
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => {
          setSearchQuery('');
          setFilterPerson('All');
          setFilterType('All');
        }} data-id="element-526">
              Clear Filters
            </Button>
          </div> : <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-slate-100" data-id="element-527">
              {paginatedHoldings.map(holding => {
            const isActive = holding.isActive ?? true;
            const country = getCountry(holding.country || 'US');
            const returnRate = holding.isRealEstate ? holding.appreciationRate ?? 0 : holding.expectedReturn ?? 0;
            const isFirstPartner = holding.person === partnerNames[0];
            const isJoint = holding.person === 'Joint';
            return <div key={holding.id} className={`p-4 ${!isActive ? 'opacity-50' : ''}`} data-id="element-528">
                    <div className="flex items-start justify-between mb-2" data-id="element-529">
                      <div className="min-w-0 flex-1 mr-3" data-id="element-530">
                        <p className="font-medium text-slate-900 truncate" data-id="element-531">
                          {holding.name}
                        </p>
                        {holding.createdAt && <p className="text-xs text-slate-400 mt-0.5" data-id="element-532">
                            Added{' '}
                            {new Date(holding.createdAt).toLocaleDateString()}
                          </p>}
                      </div>
                      <div className="text-right shrink-0" data-id="element-533">
                        <p className="font-semibold text-slate-900" data-id="element-534">
                          {formatCurrency(holding.amount, holding.currency)}
                        </p>
                        {returnRate > 0 && <p className="text-xs text-slate-500 mt-0.5" data-id="element-535">
                            {returnRate.toFixed(1)}% return
                          </p>}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mb-3" data-id="element-536">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${isJoint ? 'bg-teal-50 text-teal-700' : isFirstPartner ? 'bg-indigo-50 text-indigo-700' : 'bg-fuchsia-50 text-fuchsia-700'}`} data-id="element-537">
                        {isJoint ? '🤝 Mutual' : holding.person}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${holding.isRealEstate ? 'bg-indigo-50 text-indigo-700' : holding.type === 'Liquid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-538">
                        {holding.isRealEstate ? 'Real Estate' : holding.type}
                      </span>
                      <span className="text-xs text-slate-500" data-id="element-539">
                        {country.flag} {country.code}
                      </span>
                      {!isActive && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-500" data-id="element-540">
                          INACTIVE
                        </span>}
                    </div>
                    <div className="flex items-center justify-end gap-1" data-id="element-541">
                      {onToggleActive && <Button variant="ghost" size="sm" onClick={() => onToggleActive(holding.id)} className={`px-2 ${isActive ? 'text-slate-400' : 'text-amber-500'}`} data-id="element-542">
                          <Power className="w-4 h-4" data-id="element-543" />
                        </Button>}
                      <Button variant="ghost" size="sm" onClick={() => {
                  if (onEditHolding) onEditHolding(holding.id);
                }} className="text-slate-400 px-2" data-id="element-544">
                        <Edit2 className="w-4 h-4" data-id="element-545" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => onDelete(holding.id)} className="text-slate-400 px-2" data-id="element-546">
                        <Trash2 className="w-4 h-4" data-id="element-547" />
                      </Button>
                    </div>
                  </div>;
          })}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto" data-id="element-548">
              <table className="w-full text-sm text-left" data-id="element-549">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-550">
                  <tr data-id="element-551">
                    <th className="px-4 py-3 font-medium cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('name')} data-id="element-552">
                      Asset <SortIcon field="name" data-id="element-553" />
                    </th>
                    <th className="px-4 py-3 font-medium cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('person')} data-id="element-554">
                      Owner <SortIcon field="person" data-id="element-555" />
                    </th>
                    <th className="px-4 py-3 font-medium cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('type')} data-id="element-556">
                      Type <SortIcon field="type" data-id="element-557" />
                    </th>
                    <th className="px-4 py-3 font-medium cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('country')} data-id="element-558">
                      Country <SortIcon field="country" data-id="element-559" />
                    </th>
                    <th className="px-4 py-3 font-medium" data-id="element-560">Return</th>
                    <th className="px-4 py-3 font-medium cursor-pointer hover:bg-slate-100 transition-colors text-right" onClick={() => handleSort('amount')} data-id="element-561">
                      Amount <SortIcon field="amount" data-id="element-562" />
                    </th>
                    <th className="px-4 py-3 font-medium text-center" data-id="element-563">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-564">
                  {paginatedHoldings.map(holding => <HoldingRow key={holding.id} holding={holding} onDelete={onDelete} onUpdateAmount={onUpdateAmount} onRename={onRename} onUpdateCountry={onUpdateCountry} partnerNames={partnerNames} onEditHolding={onEditHolding} onToggleActive={onToggleActive} data-id="element-565" />)}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-white mt-auto" data-id="element-566">
                <p className="text-sm text-slate-500 hidden sm:block" data-id="element-567">
                  Showing{' '}
                  <span className="font-medium" data-id="element-568">
                    {(currentPage - 1) * itemsPerPage + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium" data-id="element-569">
                    {Math.min(currentPage * itemsPerPage, filteredAndSortedHoldings.length)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium" data-id="element-570">
                    {filteredAndSortedHoldings.length}
                  </span>
                </p>
                <p className="text-sm text-slate-500 sm:hidden" data-id="element-571">
                  {currentPage} / {totalPages}
                </p>
                <div className="flex items-center gap-1" data-id="element-572">
                  <Button variant="secondary" size="sm" className="px-2 h-8" disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} data-id="element-573">
                    <ChevronLeft className="w-4 h-4" data-id="element-574" />
                  </Button>
                  <div className="hidden sm:flex items-center gap-1 px-2" data-id="element-575">
                    {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${currentPage === page ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-id="element-576">
                        {page}
                      </button>)}
                  </div>
                  <Button variant="secondary" size="sm" className="px-2 h-8" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} data-id="element-577">
                    <ChevronRight className="w-4 h-4" data-id="element-578" />
                  </Button>
                </div>
              </div>}
          </>}
      </div>
    </div>;
}